import React from "react";
import { ExternalLink, BookMarked, FileText } from "lucide-react";

const publications = [
  {
    title: "Constrained Portfolio Optimization via Quantum Approximate Optimization Algorithm (QAOA) with XY-Mixers and Trotterized Initialization",
    venue: "arXiv",
    year: "2026",
    citations: null,
    type: "paper",
    url: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=DZxHODAAAAAJ&sortby=pubdate&citation_for_view=DZxHODAAAAAJ:Y0pCki6q_DkC",
  },
  {
    title: "Non-Convex Portfolio Optimization via Energy-Based Models (THRML) for Index Tracking",
    venue: "arXiv",
    year: "2026",
    citations: null,
    type: "paper",
    url: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=DZxHODAAAAAJ&sortby=pubdate&citation_for_view=DZxHODAAAAAJ:u-x6o8ySG0sC",
  },
  {
    title: "Enhancement of Quantum Semi-Supervised Learning via Improved Laplacian and Poisson Methods",
    venue: "arXiv",
    year: "2025",
    citations: 1,
    type: "paper",
    url: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=DZxHODAAAAAJ&sortby=pubdate&citation_for_view=DZxHODAAAAAJ:IjCSPb-OGe4C",
  },
  {
    title: "Q2SAR: A Quantum Multiple Kernel Learning Approach for Drug Discovery",
    venue: "arXiv",
    year: "2025",
    citations: 1,
    type: "paper",
    url: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=DZxHODAAAAAJ&sortby=pubdate&citation_for_view=DZxHODAAAAAJ:9yKSN-GCB0IC",
  },
  {
    title: "A Laplacian-based Quantum Graph Neural Networks for Quantum Semi-Supervised Learning",
    venue: "Quantum Information Processing",
    year: "2025",
    citations: 3,
    type: "paper",
    url: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=DZxHODAAAAAJ&sortby=pubdate&citation_for_view=DZxHODAAAAAJ:2osOgNQ5qMEC",
  },
  {
    title: "Empowering Credit Scoring Systems with Quantum-Enhanced Machine Learning",
    venue: "arXiv",
    year: "2024",
    citations: 6,
    type: "paper",
    url: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=DZxHODAAAAAJ&sortby=pubdate&citation_for_view=DZxHODAAAAAJ:qjMakFHDy7sC",
  },
  {
    title: "A Preprocessing Perspective for Quantum Machine Learning Classification Advantage in Finance Using NISQ Algorithms",
    venue: "Entropy",
    year: "2022",
    citations: 36,
    type: "paper",
    url: "https://scholar.google.com/citations?view_op=view_citation&hl=en&user=DZxHODAAAAAJ&sortby=pubdate&citation_for_view=DZxHODAAAAAJ:UeHWp8X0CEIC",
  },
];

const books = [
  {
    title: "QML Unlocked",
    subtitle: "A hands-on, code-driven guide to applying quantum computing principles to machine learning — no complex math required.",
    year: "2025",
    url: "https://a.co/d/cg9tr0U",
  },
  {
    title: "Financial Modeling Using Quantum Computing",
    subtitle: "Step-by-step guidance on quantum algorithms in Python for portfolio optimization, risk modeling, and financial problem-solving.",
    venue: "Packt Publishing",
    year: "2023",
    citations: 27,
    url: "https://a.co/d/eVi3D81",
  },
];

export default function PublicationsSection() {
  return (
    <section
      id="publications"
      data-testid="publications-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section label */}
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#34D399]/80 mb-4 block">
          // Selected Publications
        </span>

        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16 gap-4">
          <div>
            <h2
              data-testid="publications-heading"
              className="text-3xl md:text-5xl font-semibold tracking-tight text-[#F0FDF4] mb-4"
            >
              Research & Books
            </h2>
            <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl leading-relaxed">
              Peer-reviewed research and published works in quantum computing
              and applied finance.
            </p>
          </div>
          <a
            href="https://scholar.google.com/citations?hl=en&user=DZxHODAAAAAJ"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-scholar-link"
            className="inline-flex items-center gap-2 text-[#34D399] hover:text-[#10B981] font-mono text-sm transition-colors flex-shrink-0"
          >
            Google Scholar <ExternalLink size={14} />
          </a>
        </div>

        {/* Books */}
        <h3 className="text-lg font-semibold text-[#F0FDF4] mb-6 flex items-center gap-2">
          <BookMarked size={18} className="text-[#34D399]" />
          Books
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-16">
          {books.map((book, index) => (
            <a
              key={index}
              href={book.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`book-${index}`}
              className="glass-card rounded-xl p-6 group hover:-translate-y-1 transition-all duration-300 block"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h4 className="text-[#F0FDF4] font-medium text-base group-hover:text-[#34D399] transition-colors">
                    {book.title}
                  </h4>
                  {book.subtitle && (
                    <p className="text-[#94A3B8] text-xs mt-1 leading-relaxed">
                      {book.subtitle}
                    </p>
                  )}
                  <div className="flex items-center gap-3 mt-3">
                    {book.venue && (
                      <span className="text-[#94A3B8] text-xs font-mono">{book.venue}</span>
                    )}
                    <span className="text-[#94A3B8] text-xs font-mono">{book.year}</span>
                    {book.citations && (
                      <span className="text-[#34D399] text-xs font-mono">{book.citations} cited</span>
                    )}
                  </div>
                </div>
                <ExternalLink size={16} className="text-[#94A3B8] group-hover:text-[#34D399] flex-shrink-0 mt-1 transition-colors" />
              </div>
            </a>
          ))}
        </div>

        {/* Research Papers */}
        <h3 className="text-lg font-semibold text-[#F0FDF4] mb-6 flex items-center gap-2">
          <FileText size={18} className="text-[#34D399]" />
          Research Papers
        </h3>
        <div className="space-y-0">
          {publications.map((pub, index) => (
            <a
              key={index}
              href={pub.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`publication-${index}`}
              className="pub-row py-5 px-4 border-b border-[#1A2F2A]/60 flex flex-col md:flex-row md:items-center gap-2 md:gap-6 block group"
            >
              {/* Title & venue */}
              <div className="flex-grow min-w-0">
                <h4 className="text-[#F0FDF4] text-sm md:text-base font-medium leading-snug group-hover:text-[#34D399] transition-colors">
                  {pub.title}
                </h4>
                <span className="text-[#94A3B8] text-xs font-mono mt-1 block">
                  {pub.venue}
                </span>
              </div>

              {/* Year */}
              <span className="text-[#94A3B8] font-mono text-xs flex-shrink-0">
                {pub.year}
              </span>

              {/* Citations */}
              <div className="flex-shrink-0 min-w-[80px] text-right flex items-center gap-2 justify-end">
                {pub.citations ? (
                  <span className="text-[#34D399] font-mono text-xs">
                    {pub.citations} cited
                  </span>
                ) : (
                  <span className="text-[#1A2F2A] font-mono text-xs">
                    &mdash;
                  </span>
                )}
                <ExternalLink size={12} className="text-[#94A3B8] group-hover:text-[#34D399] transition-colors" />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
